import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import usePreferredTimezone from '@aurora/shared-client/components/usePreferredTimezone';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import { isEventEnded } from '@aurora/shared-client/helpers/forms/DateHelper/DateHelper';
import type { OccasionTopicMessage } from '@aurora/shared-generated/types/graphql-schema-types';
import { OccasionType } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import UrlHelper from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper';
import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { OccasionZoomMeetingVariant } from '../../../types/enums';
import type { WidgetFC } from '../../common/Widget/types';
import useAutoRefresh from '../../useAutoRefresh';
import useTranslation from '../../useTranslation';
import OccasionZoomMeeting from '../OccasionZoomMeeting/OccasionZoomMeeting';
import localStyles from './OccasionLocationAndVideoPreviewWidget.module.pcss';
import Panel, { PanelBody } from '@aurora/shared-client/components/common/Panel/Panel';

const OccasionVideoPreview = dynamic(() => import('../OccasionVideoPreview/OccasionVideoPreview'), {
  ssr: false
});

const OccasionAddToCalendarButton = dynamic(
  () => import('../OccasionAddToCalendarButton/OccasionAddToCalendarButton'),
  {
    ssr: false
  }
);

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
}

/**
 * Widget for Occasion Video Preview
 * @constructor
 *
 * @author Arjun Krishna
 */
const OccasionLocationAndVideoPreviewWidget: WidgetFC<Props> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.OCCASION_LOCATION_AND_VIDEO_PREVIEW_WIDGET
  );
  const { formatDate } = useIntl();
  const { contextMessage } = useContext(AppContext);
  const {
    liveVideoIframe,
    startTime,
    endTime,
    venue,
    location,
    isMapPreviewRequired,
    occasionType
  } = (contextMessage as OccasionTopicMessage)?.occasionData || {};
  const coverImage = (contextMessage as OccasionTopicMessage)?.coverImage;
  const isOccasionInProgress = useAutoRefresh(startTime, endTime, liveVideoIframe, 3600000);
  const tenant = useContext(TenantContext);
  const googleMapsApiKey = tenant.publicConfig.googleMapsApiKey ?? '';
  const { isAnonymous } = useRegistrationStatus();
  const isZoomOccasion = liveVideoIframe && liveVideoIframe?.match('zoom.us/j/');
  const eventEnded = isEventEnded(new Date(endTime));
  const { data: timezoneData, loading: timezoneLoading } = usePreferredTimezone();

  const userPreferredTimeZone = timezoneData?.self.profileSettings.timeZone?.value;
  if (textLoading || timezoneLoading) {
    return null;
  }

  const occasionStartTime = formatDate(startTime, {
    timeZone: userPreferredTimeZone,
    hour: 'numeric',
    minute: 'numeric'
  });

  const occasionEndTime = formatDate(endTime, {
    timeZone: userPreferredTimeZone,
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short'
  });

  const href = coverImage?.url;
  const adjustedImageUrl =
    href &&
    UrlHelper.adjustImageServerPageUrl(
      href,
      {
        height: 300,
        width: 500
      },
      true,
      null,
      false
    );

  /**
   * Renders the embedded preview of the live video as returned by embedly for the corresponding video providers.
   */
  function renderVideoPreview(): ReactElement {
    return (
      (occasionType === OccasionType.Live || occasionType === OccasionType.Hybrid) &&
      (!isAnonymous && !!liveVideoIframe && isZoomOccasion && !eventEnded ? (
        <OccasionZoomMeeting
          zoomUrl={liveVideoIframe}
          className={cx('lia-zoom-preview lia-video-preview')}
          coverImageUrl={adjustedImageUrl}
          endTime={endTime}
          variant={OccasionZoomMeetingVariant.SMALL}
        />
      ) : (
        !isZoomOccasion &&
        !!liveVideoIframe && <OccasionVideoPreview className={cx('lia-video-preview')} />
      ))
    );
  }

  /**
   * Function which renders the embedded location preview as returned by google embed API.
   */
  function renderLocationPreview(): ReactElement {
    return (
      (occasionType === OccasionType.InPerson || occasionType === OccasionType.Hybrid) &&
      isMapPreviewRequired &&
      location &&
      googleMapsApiKey && (
        <iframe
          className={cx('lia-iframe')}
          title={location}
          width="600"
          height="450"
          style={{ border: 0, borderRadius: '5px' }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(
            location
          )}`}
        />
      )
    );
  }

  /**
   * Function which renders the location metadata like venue and location information.
   */
  function renderLocationDescription(): ReactElement {
    return (
      (occasionType === OccasionType.InPerson || occasionType === OccasionType.Hybrid) &&
      (venue || location) && (
        <div className={cx('lia-g-mb-25')}>
          <h6 className={cx('lia-g-mb-0')}>{formatMessage('location')}</h6>
          {venue && <small data-testid="OccasionVenue">{venue}</small>}
          {location && (
            <div>
              <small data-testid="OccasionLocation" className={cx('text-break')}>
                {location}
              </small>
            </div>
          )}
        </div>
      )
    );
  }

  /**
   * Checks whether an event is a multi-day event or not
   * @param starttime the start time of an event
   * @param endtime the end time of an event
   * @return boolean whether an event is a multi-day or not
   */
  function isMultiDayEvent(starttime: string, endtime: string): boolean {
    const startDate = new Date(
      formatDate(startTime, {
        timeZone: userPreferredTimeZone
      })
    );
    const endDate = new Date(
      formatDate(endtime, {
        timeZone: userPreferredTimeZone
      })
    );
    return startDate?.getDate() !== endDate?.getDate();
  }

  /**
   * Function that renders the occasion start/end time and duration information.
   */
  function renderDateTimeInfo(): ReactElement {
    const isMultiDay = isMultiDayEvent(startTime, endTime);

    if (startTime && endTime && occasionStartTime && occasionEndTime) {
      return (
        <>
          <h6 className={cx('lia-g-mb-0')}>{formatMessage('dateTime')}</h6>
          <small data-testid="OccasionDuration">
            {formatDate(startTime, {
              timeZone: userPreferredTimeZone,
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            {isMultiDay ? (
              <>
                {formatMessage('occasionDurationStart', {
                  startTime: occasionStartTime
                })}
                {formatDate(endTime, {
                  timeZone: userPreferredTimeZone,
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                {formatMessage('occasionDurationEnd', {
                  endTime: occasionEndTime
                })}
              </>
            ) : (
              <small className={cx('d-block')}>
                {formatMessage('occasionDuration', {
                  startTime: occasionStartTime,
                  endTime: occasionEndTime
                })}
              </small>
            )}
          </small>
        </>
      );
    }
  }

  return (
    <Panel className={cx(className)}>
      <PanelBody>
        {liveVideoIframe && !isOccasionInProgress && renderVideoPreview()}
        {renderLocationPreview()}
        {renderLocationDescription()}
        {renderDateTimeInfo()}
        {!eventEnded && <OccasionAddToCalendarButton />}
      </PanelBody>
    </Panel>
  );
};

export default OccasionLocationAndVideoPreviewWidget;
